// Server functions for Project operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import { JsonHelpers } from '@/src/server/lib/prismaJsonHelpers'
import { generateShareId } from '@/src/server/lib/share'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import crypto from 'crypto'
import { PERMISSIONS } from '@/permission.config'
import { hasPermission } from '@/src/server/lib/permissions'
import { z } from 'zod'
import { setProjectDrawings } from '@/src/server/lib/projectDrawings'
import { OrderedDrawingSchema } from '@/shared/schemas/orderedDrawing'
import { getThumbnailUrls } from '@/src/lib/thumbnails'

// Zod schemas for input validation
const ListProjectsByOrganizationInput = z.object({
  orgId: z.string(),
})

const GetProjectInput = z.object({
  id: z.string(),
})

const GetProjectByShareIdInput = z.object({
  shareId: z.string(),
  password: z.string().optional(),
})

const CreateProjectInput = z.object({
  name: z.string(),
  description: z.string().optional(),
  company: z.string().optional(),
  fileIds: z.array(z.string()).optional(),
  orderedDrawings: z.array(OrderedDrawingSchema).optional(),
})

const EditProjectInput = z.object({
  id: z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
  company: z.string().optional(),
  fileIds: z.array(z.string()).optional(),
  orderedDrawings: z.array(OrderedDrawingSchema).optional(),
})

const UpdateProjectInput = z.object({
  projectId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  company: z.string().optional(),
})

const GetProjectDrawingsInput = z.object({
  projectId: z.string(),
})

const DeleteProjectInput = z.object({
  id: z.string(),
})

const ClaimProjectInput = z.object({
  id: z.string(),
})

const GenerateProjectPasswordInput = z.object({
  id: z.string(),
})

const RollProjectShareIdInput = z.object({
  projectId: z.string(),
})

const CopyProjectInput = z.object({
  id: z.string(),
  name: z.string().optional(),
})

const GetProjectLogsInput = z.object({
  type: z.string().optional(),
  userId: z.string().optional(),
  apiKey: z.string().optional(),
  limit: z.number().optional(),
})

// List projects - if all=true, returns all projects (admin only), otherwise returns user's own projects
export const listProjects = createServerFn({ method: 'GET' })
  .inputValidator((data?: { all?: boolean }) => data || {})
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    
    if (data?.all) {
      // SECURITY: Check permission to list all projects
      const isSystemAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
      const canListAllProjects = await hasPermission(session, [PERMISSIONS.PROJECTS_LIST_ALL, PERMISSIONS.ADMIN])
      
      if (!canListAllProjects) {
        throw new Error('Unauthorized: Admin or PROJECTS_LIST_ALL permission required for all=true mode')
      }
      
      // Build where clause based on permission level
      let whereClause: any = {}
      
      // If not global admin, filter by organization
      if (!isSystemAdmin && userId) {
        // Org owner: only see projects from users in their organization
        const user = await db.user.findUnique({
          where: { id: userId },
          select: { organizationId: true },
        })
        
        if (user?.organizationId) {
          // Get all users in the organization
          const orgUsers = await db.user.findMany({
            where: { organizationId: user.organizationId },
            select: { id: true },
          })
          const orgUserIds = orgUsers.map(u => u.id)
          
          // Filter to projects created by or shared with organization members
          whereClause = {
            OR: [
              { createdById: { in: orgUserIds } },
              { users: { some: { id: { in: orgUserIds } } } }
            ]
          }
        } else {
          // No organization - shouldn't have PROJECTS_LIST_ALL permission
          throw new Error('Unauthorized')
        }
      }
      
      const projects = await db.project.findMany({
        where: whereClause,
        orderBy: { createdAt: 'desc' },
        include: { 
          createdBy: true,
          _count: { select: { drawings: true } }
        },
      })
      return projects
    }
    
    if (!userId) return []
    const projects = await db.project.findMany({
      where: { OR: [{ createdById: userId }, { users: { some: { id: userId } } }] },
      orderBy: { createdAt: 'desc' },
      include: { 
        createdBy: true,
        _count: { select: { drawings: true } }
      },
    })
    return projects
  })

export const listProjectsByOrganization = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListProjectsByOrganizationInput.parse(data))
  .handler(async ({ data }) => {
    // SECURITY: Verify the caller belongs to this organization or is an admin
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')
    
    const caller = await db.user.findUnique({
      where: { id: userId },
      select: { organizationId: true },
    })
    
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isAdmin && caller?.organizationId !== data.orgId) {
      throw new Error('Unauthorized: You do not belong to this organization')
    }
    
    const usersInOrg = await db.user.findMany({ where: { organizationId: data.orgId }, select: { id: true } })
    const userIds = usersInOrg.map(u => u.id)
    const projects = await db.project.findMany({
      where: { OR: [{ createdById: { in: userIds } }, { users: { some: { id: { in: userIds } } } }] },
      orderBy: { createdAt: 'desc' },
      include: { 
        createdBy: true,
        _count: { select: { drawings: true, users: true } }
      },
    })
    return projects
  })

export const getProject = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    
    const project = await db.project.findUnique({ 
      where: { id: data.id }, 
      include: { files: true, users: true, createdBy: true } 
    })
    
    if (!project) return null
    
    // SECURITY: Check if user has access to this project
    if (userId) {
      const isOwner = project.createdById === userId
      const isLinked = project.users.some((u: any) => u.id === userId)
      const isSystemAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
      
      if (isOwner || isLinked || isSystemAdmin) {
        return project
      }
    }
    
    // Not authenticated or not authorized
    throw new Error('Unauthorized: You do not have access to this project')
  })

export const getProjectByShareId = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetProjectByShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const expectedHash = crypto.createHash('sha256').update(data.shareId).digest('hex')
    if (data.password !== expectedHash) throw new Error('Invalid password')
    const project = await db.project.findFirst({ where: { shareId: data.shareId }, include: { files: true, users: true } })
    return project
  })

export const createProject = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => CreateProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')
    
    // SECURITY: Check permission to create projects
    const canCreateProjects = await hasPermission(session, [PERMISSIONS.PROJECTS_MANAGE, PERMISSIONS.ADMIN])
    if (!canCreateProjects) {
      throw new Error('Unauthorized: Project creation requires subscriber or higher access')
    }
    
    const shareId = await generateShareId({ prefix: 'P', length: 8, isIdAvailable: async (id) => !(await db.project.findUnique({ where: { shareId: id } })) })
    const drawingIds = data.orderedDrawings?.map(d => d.drawingId) || [];
    const project = await db.project.create({
      data: {
        shareId,
        name: data.name,
        description: data.description ?? '',
        company: data.company ?? '',
        createdById: userId!,
        files: data.fileIds ? { connect: data.fileIds.map(id => ({ id })) } : undefined,
        drawings: drawingIds.length > 0 ? { connect: drawingIds.map(id => ({ id })) } : undefined,
        orderedDrawings: data.orderedDrawings || [],
      },
    })
    
    // Log the project creation
    db.logEntry.create({
      data: {
        type: 'CreateProject',
        userId,
        details: {
          projectId: project.id,
          projectName: project.name,
          fileCount: data.fileIds?.length ?? 0,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))
    
    return project
  })

export const editProject = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => EditProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const project = await db.project.findUnique({ where: { id: data.id }, include: { users: true } })
    if (!project) throw new Error('Project not found')
    
    // Check permission
    const isOwner = project.createdById === userId
    const isLinked = project.users.some(u => u.id === userId)
    const isAdmin = (session?.user as any)?.permissions?.includes('ADMIN')
    if (!isOwner && !isLinked && !isAdmin) throw new Error('Not authorized')
    
    console.log(`[editProject] Updating project ${data.id}`);
    
    const updateData: any = {
      name: data.name,
      description: data.description,
      company: data.company,
    };
    
    // Only update files if provided
    if (data.fileIds !== undefined) {
      updateData.files = { set: data.fileIds.map(id => ({ id })) };
    }
    
    // Update basic project info
    const updatedProject = await db.project.update({
      where: { id: data.id },
      data: updateData,
    });
    
    // Update drawings using helper to maintain both relation and JSON
    if (data.orderedDrawings !== undefined) {
      await setProjectDrawings(data.id, data.orderedDrawings);
    }
    
    return updatedProject
  })

export const updateProject = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpdateProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')
    
    const project = await db.project.findUnique({ 
      where: { id: data.projectId }, 
      include: { users: true } 
    })
    if (!project) throw new Error('Project not found')
    
    // Check permission
    const isOwner = project.createdById === userId
    const isLinked = project.users.some(u => u.id === userId)
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isOwner && !isLinked && !isAdmin) throw new Error('Not authorized')
    
    return await db.project.update({
      where: { id: data.projectId },
      data: {
        name: data.name,
        description: data.description,
        company: data.company,
      },
    })
  })

export const getProjectDrawings = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetProjectDrawingsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')
    
    const project = await db.project.findUnique({ 
      where: { id: data.projectId }, 
      include: { users: true } 
    })
    if (!project) throw new Error('Project not found')
    
    // Check permission
    const isOwner = project.createdById === userId
    const isLinked = project.users.some(u => u.id === userId)
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isOwner && !isLinked && !isAdmin) throw new Error('Not authorized')
    
    // Fetch drawings connected via the many-to-many relation
    const drawings = await db.drawing.findMany({
      where: { 
        projects: { some: { id: data.projectId } }
      },
      select: {
        id: true,
        shareId: true,
        name: true,
        number: true,
        company: true,
        privacy: true,
        status: true,
        error: true,
        createdAt: true,
        completedAt: true,
        overrides: true,
        // Include master for fallback name/number resolution
        master: {
          select: {
            id: true,
            confidenceScore: true,
            data: true,
          },
        },
        // Include files for thumbnail and filename
        files: {
          take: 1,
          select: {
            id: true,
            name: true,
          },
        },
        operationEntries: {
          where: { deletedAt: null },
          select: {
            id: true,
            name: true,
            operationTypeId: true,
          },
          orderBy: { createdAt: 'asc' },
        },
      },
      orderBy: { createdAt: 'desc' },
    })
    
    console.log(`[getProjectDrawings] Project ${data.projectId}: Found ${drawings.length} drawings via relation`)
    
    // Check if orderedDrawings JSON references drawings not in the relation (data inconsistency)
    if (project.orderedDrawings && Array.isArray(project.orderedDrawings)) {
      const orderedIds = (project.orderedDrawings as any[]).map((od: any) => od.drawingId)
      const fetchedIds = new Set(drawings.map(d => d.id))
      const missingIds = orderedIds.filter(id => !fetchedIds.has(id))
      
      if (missingIds.length > 0) {
        console.warn(`[getProjectDrawings] ⚠️ DATA INCONSISTENCY: ${missingIds.length} drawings in orderedDrawings JSON are not in the relation`)
        console.warn('[getProjectDrawings] Missing IDs:', missingIds)
        
        // Fetch the missing drawings directly (they exist but relation is broken)
        const missingDrawings = await db.drawing.findMany({
          where: { id: { in: missingIds } },
          select: {
            id: true,
            shareId: true,
            name: true,
            number: true,
            company: true,
            privacy: true,
            status: true,
            error: true,
            createdAt: true,
            completedAt: true,
            overrides: true,
            // Include master for fallback name/number resolution
            master: {
              select: {
                id: true,
                confidenceScore: true,
                data: true,
              },
            },
            // Include files for thumbnail and filename
            files: {
              take: 1,
              select: {
                id: true,
                name: true,
              },
            },
            operationEntries: {
              where: { deletedAt: null },
              select: {
                id: true,
                name: true,
                operationTypeId: true,
              },
              orderBy: { createdAt: 'asc' },
            },
          },
        })
        
        if (missingDrawings.length > 0) {
          console.log(`[getProjectDrawings] ✅ Found ${missingDrawings.length} missing drawings, auto-repairing relation...`)
          
          // Auto-repair: add missing drawings to the relation
          await db.project.update({
            where: { id: data.projectId },
            data: {
              drawings: {
                connect: missingDrawings.map(d => ({ id: d.id }))
              }
            }
          })
          
          // Include the missing drawings in the result
          drawings.push(...missingDrawings)
        }
      }
    }
    
    // Transform drawings to add computed fields (thumbnails, fileName, fileId)
    const transformedDrawings = drawings.map((drawing: any) => {
      const firstFile = drawing.files?.[0]
      let thumbnailSmallUrl: string | null = null
      let thumbnailLargeUrl: string | null = null
      
      if (firstFile) {
        const thumbnails = getThumbnailUrls(firstFile.id)
        thumbnailSmallUrl = thumbnails.small
        thumbnailLargeUrl = thumbnails.large
      }
      
      return {
        ...drawing,
        operations: drawing.operationEntries ?? [],
        fileName: firstFile?.name ?? null,
        fileId: firstFile?.id ?? null,
        thumbnailSmallUrl,
        thumbnailLargeUrl,
      }
    })
    
    console.log(`[getProjectDrawings] Returning ${transformedDrawings.length} transformed drawings`)
    
    return { drawings: transformedDrawings }
  })

export const deleteProject = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const project = await db.project.findUnique({ where: { id: data.id }, include: { users: true } })
    if (!project) throw new Error('Project not found')
    
    const isOwner = project.createdById === userId
    const isLinked = project.users.some(u => u.id === userId)
    if (!isOwner && !isLinked) throw new Error('Not authorized')
    
    await db.project.delete({ where: { id: data.id } })
    return { success: true }
  })

export const claimProject = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ClaimProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')
    
    return await db.project.update({
      where: { id: data.id },
      data: { users: { connect: { id: userId } } },
    })
  })

export const generateProjectPassword = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GenerateProjectPasswordInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const project = await db.project.findUnique({ where: { id: data.id } })
    if (!project) throw new Error('Project not found')
    
    const isOwner = project.createdById === userId
    const isAdmin = (session?.user as any)?.permissions?.includes('ADMIN')
    if (!isOwner && !isAdmin) throw new Error('Not authorized')
    
    const password = crypto.createHash('sha256').update(project.shareId!).digest('hex')
    return { password }
  })

export const rollProjectShareId = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => RollProjectShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    if (!session?.user?.id) throw new Error('Unauthorized')

    const project = await db.project.findUnique({ where: { id: data.projectId } })
    if (!project) throw new Error('Project not found')

    const isOwner = project.createdById === session.user.id
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isOwner && !isAdmin) throw new Error('Not authorized')

    const newShareId = await generateShareId({
      prefix: 'P',
      length: 8,
      isIdAvailable: async (id) => !(await db.project.findUnique({ where: { shareId: id } })),
    })

    await db.project.update({ where: { id: data.projectId }, data: { shareId: newShareId } })
    return { shareId: newShareId }
  })

// Copy a project — creates a new project with the same drawings/files but no shared users
export const copyProject = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => CopyProjectInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')

    const source = await db.project.findUnique({
      where: { id: data.id },
      include: {
        files: { select: { id: true } },
        drawings: { select: { id: true } },
        users: { select: { id: true } },
      },
    })
    if (!source) throw new Error('Project not found')

    // Check access
    const isOwner = source.createdById === userId
    const isLinked = source.users.some(u => u.id === userId)
    const isSystemAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    if (!isOwner && !isLinked && !isSystemAdmin) throw new Error('Not authorized')

    const shareId = await generateShareId({
      prefix: 'P',
      length: 8,
      isIdAvailable: async (id) => !(await db.project.findUnique({ where: { shareId: id } })),
    })

    const newProject = await db.project.create({
      data: {
        shareId,
        name: data.name || `${source.name} (Copy)`,
        description: source.description,
        company: source.company,
        createdById: userId,
        files: source.files.length > 0 ? { connect: source.files.map(f => ({ id: f.id })) } : undefined,
        drawings: source.drawings.length > 0 ? { connect: source.drawings.map(d => ({ id: d.id })) } : undefined,
        orderedDrawings: source.orderedDrawings ?? undefined,
      },
    })

    db.logEntry.create({
      data: {
        type: 'CopyProject',
        userId,
        details: {
          sourceProjectId: source.id,
          newProjectId: newProject.id,
          newProjectName: newProject.name,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))

    return newProject
  })

export const getProjectLogs = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetProjectLogsInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    if (!await hasPermission(session, [PERMISSIONS.ADMIN])) {
      throw new Error('Unauthorized')
    }
    const where: Record<string, any> = {}
    if (data?.type) where.type = data.type
    if (data?.userId) where.userId = data.userId
    if (data?.apiKey) where.apiKey = data.apiKey
    const logs = await db.logEntry.findMany({
      where,
      take: data?.limit ?? 100,
      orderBy: { createdAt: 'desc' },
    })
    return logs.map(l => ({
      ...l,
      createdAt: l.createdAt.toISOString(),
      details: JsonHelpers.normalizeRecordToObjectValues(JsonHelpers.logDetails.fromDb(l.details)),
    }))
  })
